import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Markdown from 'markdown-to-jsx';
import classnames from 'classnames';
import './FaqQuestion.scss';
import TextArea from 'components/inputs/TextArea';
import TextField from 'components/inputs/TextField';
import Icon from 'components/Icon';
import { useDispatch, useSelector } from 'react-redux';
import ButtonNew from 'components/inputs/ButtonNew';
import { saveFaq, deleteQuestion } from 'actions/faq';
import { faqEditingInitialState } from 'constants/faq';
import FaqDeleteModal from './FaqDeleteModal';
import {
  startEdit,
  setField,
  cancelEdit,
  finishEdit,
} from 'actions/faqSettings';

const FaqEditor = ({ onChange, value, orientation = 'vertical' }) => {
  const [tempValue, setTempValue] = useState(value);

  const onComplete = useCallback(() => {
    onChange(tempValue);
    setTempValue(null);
  }, [onChange, tempValue]);

  const onCancel = useCallback(() => {
    onChange(value);
    setTempValue(null);
  }, [onChange, value]);

  const onEnter = useCallback(
    event => {
      if (event.keyCode === 13 && !event.shiftKey) {
        onComplete();
      }
    },
    [onComplete]
  );

  return (
    <div
      className={classnames('FaqEditor', {
        vertical: orientation === 'vertical',
        horizontal: orientation !== 'vertical',
      })}
    >
      {orientation === 'vertical' ? (
        <TextArea
          onChange={setTempValue}
          value={tempValue ?? value}
          onKeyDown={onEnter}
        />
      ) : (
        <TextField
          onChange={setTempValue}
          value={tempValue ?? value}
          onKeyDown={onEnter}
        />
      )}
      <div className="FaqEditor-button-container">
        <ButtonNew
          label="Ok"
          buttonClasses="secondary-button-small"
          onClick={onComplete}
        />
        <ButtonNew
          label="Cancel"
          buttonClasses="secondary-button-small"
          onClick={onCancel}
        />
      </div>
    </div>
  );
};

const FaqQuestion = ({
  sectionId,
  questionAnswer,
  editable,
  setEditing,
  editing,
  activeFaqSection,
  isAdmin,
}) => {
  const dispatch = useDispatch();
  const editingFaq = useSelector(state => state.faqSettings.faq);
  const { order, question, answer, id } = questionAnswer;

  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(order === 0 ? true : false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(null);

  useEffect(() => {
    if (sectionId) {
      setIsOpen(order === 0 ? true : false);
    }
  }, [sectionId, order]);

  const encode = str => {
    const encoded = btoa(encodeURIComponent(str));
    return encoded;
  };

  const onUpdate = useCallback(
    nextValue => {
      const encodedValue = encode(nextValue);
      const currentValue = questionAnswer?.[editing?.key];
      if (
        encodedValue !== undefined &&
        currentValue !== undefined &&
        encodedValue !== currentValue
      ) {
        const next = { ...questionAnswer, [editing?.key]: encodedValue };

        const nextContent = editingFaq?.content.map(c =>
          c?.id === next?.id ? next : c
        );

        const nextFaq = {
          ...editingFaq,
          content: nextContent,
        };

        dispatch(setField('content', nextContent));
        dispatch(finishEdit(nextFaq));
        dispatch(saveFaq(nextFaq?.id));
      } else {
        dispatch(cancelEdit());
      }
      setEditing(faqEditingInitialState);
    },
    [dispatch, editingFaq, setEditing, editing, questionAnswer]
  );

  const decodedQuestion = useMemo(() => {
    if (!question) return '';
    try {
      return decodeURIComponent(atob(question));
    } catch (err) {
      return '';
    }
  }, [question]);

  const decodedAnswer = useMemo(() => {
    if (!answer) return '';
    try {
      return decodeURIComponent(atob(answer));
    } catch (err) {
      return '';
    }
  }, [answer]);

  const editingQuestion = useMemo(() => {
    const isEditing = editing?.questionAnswer?.id === questionAnswer?.id;
    const isAnswer = editing?.key === 'question';

    return isEditing && isAnswer;
  }, [questionAnswer, editing]);

  const editingAnswer = useMemo(() => {
    const isEditing = editing?.questionAnswer?.id === questionAnswer?.id;
    const isAnswer = editing?.key === 'answer';

    return isEditing && isAnswer;
  }, [questionAnswer, editing]);

  const onDelete = useCallback(() => {
    const questionId = deleteModalOpen;
    dispatch(deleteQuestion(sectionId, questionId));
    dispatch(saveFaq(sectionId));
  }, [dispatch, sectionId, deleteModalOpen]);

  const onCancelDelete = useCallback(() => {
    setDeleteModalOpen(null);
  }, []);

  // Start edit on question answer change
  useEffect(() => {
    if (!questionAnswer) return;
    dispatch(startEdit(activeFaqSection));
  }, [dispatch, activeFaqSection, questionAnswer]);

  return (
    <div
      className={classnames('FaqQuestion', {
        active: isOpen,
        hovered: isHovered,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="FaqQuestion-question-container">
        {editable && isHovered && !editingQuestion ? (
          <ButtonNew
            buttonClasses="icon-button-small"
            onClick={() =>
              setEditing(v => ({
                sectionId,
                questionAnswer,
                key: 'question',
              }))
            }
            icon="pencil"
          />
        ) : null}

        <div className="FaqQuestion-number">{Number(order) + 1}</div>

        {editingQuestion ? (
          <div className="FaqQuestion-question-container">
            {/* TODO this editor looks bad for questions */}
            <FaqEditor
              onChange={onUpdate}
              value={decodedQuestion}
              orientation={'horizontal'}
            />
          </div>
        ) : (
          <div
            className="FaqQuestion-question-container"
            onClick={() => setIsOpen(!isOpen)}
          >
            <div
              className={classnames('FaqQuestion-question', { active: isOpen })}
            >
              {decodedQuestion}
            </div>
            <div className="FaqQuestion-icon">
              <Icon alt={'accordion'} icon={isOpen ? 'faqMinus' : 'faqPlus'} />
            </div>
          </div>
        )}

        {isAdmin && isHovered && !editingQuestion ? (
          <ButtonNew
            buttonClasses="icon-button-small"
            onClick={() => setDeleteModalOpen(questionAnswer?.id)}
            icon="trash"
          />
        ) : null}
      </div>

      {isOpen ? (
        editable ? (
          editingAnswer ? (
            <FaqEditor onChange={onUpdate} value={decodedAnswer} />
          ) : (
            <div className="FaqQuestion-answer-container">
              {isHovered ? (
                <ButtonNew
                  buttonClasses="icon-button-small"
                  onClick={() =>
                    setEditing(v => ({
                      sectionId,
                      questionAnswer,
                      key: 'answer',
                    }))
                  }
                  icon="pencil"
                />
              ) : null}
              <div className="FaqQuestion-answer">
                <Markdown>{decodedAnswer}</Markdown>
              </div>
            </div>
          )
        ) : (
          <div className="FaqQuestion-answer-container">
            <div className="FaqQuestion-icon"></div>
            <div className="FaqQuestion-answer">{decodedAnswer}</div>
          </div>
        )
      ) : null}

      {deleteModalOpen ? (
        <FaqDeleteModal
          text={`Are you sure you want to delete this FAQ?`}
          onConfirm={onDelete}
          onCancel={onCancelDelete}
        />
      ) : null}
    </div>
  );
};

export default FaqQuestion;
